// We use this type for data we fetch from backend. We can show skeleton loader because it has three states:
// - undefined — show skeleton loader
// - [].length === 0 — show data
// - [].length > 0 — show empty message
export type FetchedItems<T> = T[] | undefined;
export type FetchedItem<T> = T | undefined;
// --

export interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  remoteAddr: string;
  totpEnabled: boolean;
  hasTotpRequiredOrg: boolean;
  hasRecoveryCodes: boolean;
  isDemo: boolean;
}

export interface InviteData {
  userExists: boolean;
  invitedUserEmail: string;
  organizationToJoin: { id: string; name: string };
}

export enum Role {
  ADMIN = 'admin',
  MEMBER = 'member',
  OWNER = 'owner',
  DEMO = 'demo',
}

export interface Organization {
  id: string;
  name: string;
  userRole: Role;
  totpRequired: boolean;
}

export interface OrganizationMember {
  user: User;
  role: Role;
}

export interface Pagination<T> {
  count: number;
  next: string;
  previous: string;
  results: T[];
}

export type Coordinates = [number, number];

export interface Option<T = any> {
  name: string;
  data: T;
  id: string;
}

export type GetControllersParams = Partial<{
  page: number;
  serialNumber: string;
  groupName: string;
  description: string;
  firmwareVersion: string;
  ordering: string;
  groupIds: string;
  pageSize: number;
  latitudeMax: number;
  latitudeMin: number;
  longitudeMax: number;
  longitudeMin: number;
}> & { organizationId: string };

export interface UserDefinedDataItem {
  label: string; value: string;
}

interface BackendControllerBase {
  serialNumber: string;
  firmwareVersion: string;
  previewUrl: string | null;
  isAgentOk: boolean;
  showMetricsUpdateBanner?: boolean;
  activeTransferRequestId: string | null;
  lastAgentStartupAt: string | null;
  lastAgentPingAt: string | null;
  lastMetricsAt: string | null;
  description: string;
  metricsEnabled: boolean;
  metricsSendIntervalSeconds?: number;
  userDefinedData: UserDefinedDataItem[];
  organization: Pick<Organization, 'id' | 'totpRequired'>;
  group: {
    id: string;
    name: string;
    level: number;
  } | null;
}

interface BackendControllerWithCoordinates extends BackendControllerBase {
  latitude: string;
  longitude: string;
}

interface BackendControllerWithoutCoordinates extends BackendControllerBase {
  latitude: null;
  longitude: null;
}

export type BackendController =
  | BackendControllerWithCoordinates
  | BackendControllerWithoutCoordinates;

export interface Controller
  extends Omit<
    BackendController,
    | 'latitude'
    | 'longitude'
    | 'showMetricsUpdateBanner'
    | 'metricsEnabled'
    | 'metricsSendIntervalSeconds'
  > {
  coordinates: Coordinates | null;
  showMetricsUpdateBanner: boolean;
  metricsEnabled: boolean;
  metricsSendIntervalSeconds: number;
}

export interface ControllerWithCoordinates extends Controller {
  coordinates: Coordinates;
}

// PATCH answers with the update serializer, not a controller. Never widen to BackendController.
export interface UpdatedController {
  description: BackendController['description'];
  group: string | null;
  latitude: BackendController['latitude'];
  longitude: BackendController['longitude'];
  userDefinedData: BackendController['userDefinedData'];
  metricsEnabled: boolean;
  metricsSendIntervalSeconds?: number;
}

export type ControllerEditable = Partial<{
  description: BackendController['description'];
  group: string | null;
  latitude: BackendController['latitude'];
  longitude: BackendController['longitude'];
  userDefinedData: BackendController['userDefinedData'];
  metricsEnabled: boolean;
  metricsSendIntervalSeconds: number;
}>;

export interface LoginResponse {
  access: string;
  refresh: string;
}

export interface Mqtt {
  topic: string;
  timestamp: string;
  value: string;
}

export interface Group {
  id: string;
  name: string;
  level: number;
  organization: Pick<Organization, 'id' | 'totpRequired'>;
  subgroups: Group[];
}

export interface TimeseriesPoint {
  timestamp: string;
  value: string;
}

export type DiagnosticStatus =
  | 'cannot_be_processed'
  | 'not_requested'
  | 'processing'
  | 'ready'
  | 'error';

export interface Diagnostic {
  status: DiagnosticStatus | null;
  id: string | null;
  created: string | null;
}

export type SystemMetricName =
  | 'load15'
  | 'mem_available'
  | 'disk_free_root'
  | 'disk_free_data';

export interface TimeseriesRequestPayload {
  start: string;
  stop: string;
  name: SystemMetricName;
}

export enum TunnelProtocol {
  SSH = 'ssh',
  HTTP = 'http',
}

export interface Tunnel {
  tunnel: {
    id: string;
    connection: {
      id: string;
      lastPingAt: string | null;
      token: string;
    };
    connectedAt: string | null;
    protocol: TunnelProtocol;
    port: number;
  };
  tunnelKey: string | null;
  tunnelRedirectUrl: string | null;
}

export interface UserEditableData {
  userEmail: string;
  userPassword: string;
  organizationInviteToken: string | null;
}

export interface UserChangeEmailData {
  newUserEmail: string;
  isPrimary: boolean;
}

export interface ServiceCatalogItem {
  port: number;
  name: string;
}

export interface ControllerService {
  id: number;
  port: number;
  name: string | null;
  displayName: string;
}

export type GraphHealthcheck = (value: number) => boolean;
export type GraphFormatter = (value: number) => string;

export interface GraphPreset {
  request: Omit<TimeseriesRequestPayload, 'start' | 'stop'>;
  healthcheck: GraphHealthcheck;
  formatter?: GraphFormatter;
  label: string;
  yMin?: number;
}

export interface GeosearchItem {
  id: string;
  name: string;
  location: Coordinates;
}

export interface Count {
  count: number;
  countAgentNotOk: number;
}

export interface TotalCount {
  totalCount: number;
}

export interface SetupTotpResponse {
  qrCode: string;
  secret: string;
  uri: string;
}

export interface ActivateTotpResponse {
  detail: string;
  recoveryCodes: string[];
}

export interface AuthSession {
  id: string;
  ipAddress: string;
  userAgent: string;
  country: string;
  city: string;
  created: string;
  lastActivity: string;
  isCurrent: boolean;
}

export interface TerminateSessionsResponse {
  detail: string;
  terminatedCount: number;
}

export type FeatureBadgeColor =
  | 'violet'
  | 'blue'
  | 'green'
  | 'orange'
  | 'red'
  | 'inherit';

export interface FeatureBadgeText {
  ru: string;
  en: string;
}

export interface FeatureBadge {
  label: FeatureBadgeText;
  description: FeatureBadgeText | null;
  color: FeatureBadgeColor;
  until: string | null;
}

export interface InterfaceLanguage {
  code: string;
  name: string;
}

export interface Environments {
  /**
   * Is totp allowed in this environment. If not, then hide the option to enable two-factor authentication
   */
  TOTP_AUTH_ENABLED: boolean;
  IS_ON_PREMISE: boolean;
  ON_PREMISE_VERSION: string;
  MAX_ACTIVATED_CONTROLLERS: number;
  FEATURE_BADGES?: Record<string, FeatureBadge>;
  /**
   * Languages the admin left on, in the order they should be offered.
   */
  LANGUAGES: InterfaceLanguage[];
  DEFAULT_LANGUAGE: string;
}

interface StatusPageIncident {
  id: number;
  title: string;
  content: string;
  style?: string;
  active?: boolean;
  lastUpdatedDate?: string | null;
}

interface StatusPageMaintenance {
  id: number;
  title: string;
  description: string;
  status: string;
}

export interface StatusPagePayload {
  incidents?: StatusPageIncident[];
  maintenanceList?: StatusPageMaintenance[];
}

export interface CloudStatusNotice {
  key: string;
  title: string;
  text: string;
  severity: 'info' | 'warn' | 'error';
}

interface TransferredController {
  id: string;
  serialNumber: string;
}

export interface ControllersTransfer {
  transferRequestId: string;
  targetEmail: string;
  controllers: TransferredController[];
  status: string;
  createdAt: string;
  expiresAt: string;
}
